import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "../ui/command";

/**
 * BuilderCommandPalette — global Cmd/Ctrl+K command palette.
 *
 * Reuses the SAME items shape consumed by <BuilderOverflowMenu />:
 *   items = [{ label, actions: [{ id, label, icon, onClick|href, disabled, tone, testId }] }]
 *
 * Any builder (or any component) can register its actions into the palette by
 * wrapping its tree with <CommandPaletteScope items={...}> or by calling the
 * imperative hook useRegisterCommandItems(items). When the component unmounts,
 * its registration is cleaned up so the palette only exposes actions for the
 * currently-open builder.
 *
 * Keyboard:
 *   Cmd/Ctrl + K → open
 *   Esc           → close
 *   Enter         → run highlighted item
 *
 * Mount <BuilderCommandPaletteProvider /> high in the React tree (Layout.jsx).
 */

const CommandRegistryCtx = createContext(null);

export function BuilderCommandPaletteProvider({ children }) {
  const [open, setOpen] = useState(false);
  // Registry is keyed by a stable token per scope so we can merge from
  // multiple sources. Each entry has { items, weight } for future sort.
  const [scopes, setScopes] = useState({});

  const register = useCallback((token, items) => {
    setScopes(prev => ({ ...prev, [token]: items || [] }));
  }, []);
  const unregister = useCallback((token) => {
    setScopes(prev => {
      const { [token]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  // Global Cmd/Ctrl+Shift+P binding — matches VS Code-style command palettes.
  // We avoid Cmd/Ctrl+K because <GlobalSearch> already binds that keystroke
  // for app-wide fuzzy search.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.shiftKey && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        setOpen(o => !o);
      }
      // `?` on pages that aren't a text field opens too (classic help hint)
      if (e.key === "?" && !["input", "textarea", "select"].includes(tag) && !e.target?.isContentEditable) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const mergedItems = useMemo(() => {
    const out = [];
    for (const token of Object.keys(scopes)) {
      for (const section of scopes[token] || []) {
        if (!Array.isArray(section?.actions) || section.actions.length === 0) continue;
        out.push({ ...section, _scope: token });
      }
    }
    return out;
  }, [scopes]);

  const run = (action) => {
    setOpen(false);
    if (action.disabled) return;
    if (action.href) {
      window.open(action.href, action.target || "_blank", "noopener,noreferrer");
      return;
    }
    try { action.onClick?.(); } catch (e) { console.error("command failed", e); }
  };

  const value = useMemo(() => ({ register, unregister, openPalette: () => setOpen(true) }), [register, unregister]);

  return (
    <CommandRegistryCtx.Provider value={value}>
      {children}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Run a command…  (⌘⇧P)" data-testid="command-palette-input" />
        <CommandList data-testid="command-palette-list">
          <CommandEmpty>No matching commands.</CommandEmpty>
          {mergedItems.map((section, i) => (
            <CommandGroup key={i} heading={section.label || ""}>
              {section.actions.map(action => (
                <CommandItem
                  key={`${section._scope}-${action.id}`}
                  disabled={action.disabled}
                  onSelect={() => run(action)}
                  value={`${section.label || ""} ${typeof action.label === "string" ? action.label : action.id}`}
                  data-testid={`command-palette-item-${action.testId || action.id}`}
                >
                  {action.icon && <span className="mr-2 inline-flex opacity-80">{action.icon}</span>}
                  <span>{action.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </CommandRegistryCtx.Provider>
  );
}

/**
 * useRegisterCommandItems — hook form. Pass an items array (same shape as
 * BuilderOverflowMenu) and a stable scope token. Items re-register whenever
 * the items array reference changes; cleaned up on unmount.
 *
 *   const items = useMemo(() => [...], [deps]);
 *   useRegisterCommandItems("pitch-editor", items);
 */
export function useRegisterCommandItems(scopeToken, items) {
  const ctx = useContext(CommandRegistryCtx);
  const lastRef = useRef();
  useEffect(() => {
    if (!ctx || !scopeToken) return;
    ctx.register(scopeToken, items);
    lastRef.current = scopeToken;
    return () => ctx.unregister(scopeToken);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, scopeToken, items]);
}

/**
 * useCommandPalette — imperative access (e.g., to open from a custom shortcut
 * in a specific builder).
 */
export function useCommandPalette() {
  return useContext(CommandRegistryCtx);
}
